import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { LinkOutlined } from "@ant-design/icons";
import type { LeanCustomsDeclaration } from "../../services/leancustomsService";
import { LEANCUSTOMS_DECLARATION_BASE } from "../../constants/leancustoms";
import {
  DECLARATION_STATUS_COLORS,
  DECLARATION_STATUS_LABELS,
} from "../../types/leancustoms";

export interface BookingCustomsLeanDeclarationMatchesPanelProps {
  bookingId: number;
  bookingReference: string;
  declarations: LeanCustomsDeclaration[];
  loading: boolean;
  linking: boolean;
  existingDeclarationId?: string | null;
  onLinkDeclaration: (declarationId: string) => void | Promise<void>;
}

const declarationColumns: ColumnsType<LeanCustomsDeclaration> = [
  {
    title: "Reference",
    dataIndex: "reference",
    key: "reference",
    render: (ref: string) => (
      <Typography.Text code style={{ fontSize: 12 }}>
        {ref || "—"}
      </Typography.Text>
    ),
  },
  {
    title: "Declaration ID",
    key: "id",
    width: 130,
    ellipsis: true,
    render: (_, d) => (
      <Typography.Text code copyable={{ text: d.id }} style={{ fontSize: 12 }}>
        {d.id}
      </Typography.Text>
    ),
  },
  {
    title: "Type",
    dataIndex: "declaration_type",
    key: "declaration_type",
    width: 72,
    render: (type: string) => type || "—",
  },
  {
    title: "Status",
    key: "dms_status",
    width: 140,
    render: (_, d) => {
      const status = d.dms_status || "unknown";
      return (
        <Tag color={DECLARATION_STATUS_COLORS[status] || "default"}>
          {DECLARATION_STATUS_LABELS[status] || status}
        </Tag>
      );
    },
  },
  {
    title: "Updated",
    key: "updated_at",
    width: 132,
    render: (_, d) => {
      const t = d.updated_at ?? d.created_at;
      return t ? dayjs(t).format("YYYY-MM-DD HH:mm") : "—";
    },
  },
  {
    title: "Open",
    key: "open",
    width: 72,
    render: (_, d) => (
      <Button
        type="link"
        size="small"
        icon={<LinkOutlined />}
        href={`${LEANCUSTOMS_DECLARATION_BASE}/${d.id}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ padding: 0, height: "auto" }}
      >
        LC
      </Button>
    ),
  },
];

export const BookingCustomsLeanDeclarationMatchesPanel: React.FC<
  BookingCustomsLeanDeclarationMatchesPanelProps
> = ({
  bookingId,
  bookingReference,
  declarations,
  loading,
  linking,
  existingDeclarationId,
  onLinkDeclaration,
}) => {
  const { token } = theme.useToken();
  const [selectedDeclarationId, setSelectedDeclarationId] = useState<string | null>(
    null
  );

  useEffect(() => {
    setSelectedDeclarationId((prev) => {
      const ids = new Set(declarations.map((d) => d.id).filter(Boolean));
      if (prev && ids.has(prev)) return prev;
      return declarations[0]?.id ?? null;
    });
  }, [declarations]);

  const declarationFound = declarations.length > 0;
  const alreadyLinked =
    !!selectedDeclarationId &&
    !!existingDeclarationId &&
    String(selectedDeclarationId) === String(existingDeclarationId);
  const canLink =
    !loading && declarationFound && !!selectedDeclarationId && !alreadyLinked;

  const cardStyle = useMemo(
    () => ({
      borderColor: declarationFound ? token.colorSuccess : token.colorWarning,
    }),
    [declarationFound, token.colorSuccess, token.colorWarning]
  );

  if (loading) {
    return <Spin size="small" />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {declarationFound && (
        <Alert
          type="info"
          showIcon
          message="Matching Lean Customs declaration"
          description={
            <>
              Found a declaration for{" "}
              <Typography.Text code>{bookingReference}</Typography.Text> (
              <Typography.Text code>EFF{bookingId}</Typography.Text>), but this booking
              does not have a declaration ID saved yet. Select a row and use{" "}
              <strong>Link declaration to booking</strong> so customers see it on the
              booking view.
            </>
          }
        />
      )}

      <Card
        size="small"
        title={
          <Space size={8} wrap>
            <span>Declaration</span>
            <Tag color={declarationFound ? "success" : "warning"}>
              {declarationFound ? "Match found" : "No match"}
            </Tag>
          </Space>
        }
        styles={{ body: { padding: 12 } }}
        style={cardStyle}
      >
        {declarationFound ? (
          <Table<LeanCustomsDeclaration>
            size="small"
            rowKey="id"
            columns={declarationColumns}
            dataSource={declarations}
            pagination={
              declarations.length > 6
                ? { pageSize: 6, size: "small", showSizeChanger: false }
                : false
            }
            scroll={{ x: "max-content" }}
            rowSelection={{
              type: "radio",
              selectedRowKeys: selectedDeclarationId ? [selectedDeclarationId] : [],
              onChange: (keys) => {
                const k = keys[0];
                setSelectedDeclarationId(typeof k === "string" ? k : null);
              },
            }}
          />
        ) : (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            No declaration matched{" "}
            <Typography.Text code>{bookingReference}</Typography.Text> in this search.
          </Typography.Paragraph>
        )}
      </Card>

      {declarationFound && (
        <>
          <Button
            type="default"
            disabled={!canLink}
            loading={linking}
            onClick={() => {
              if (selectedDeclarationId) void onLinkDeclaration(selectedDeclarationId);
            }}
          >
            Link declaration to booking
          </Button>
          {!canLink && alreadyLinked && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This declaration is already linked to the booking.
            </Typography.Text>
          )}
        </>
      )}
    </Space>
  );
};
